import { LoaderFunctionArgs } from '@remix-run/node';
import Layout from '~/components/layout';
import { validateUser } from '~/services/api/auth';
import { getCategoryList } from '~/services/api/categories';
import { Footer } from 'pdfs/components/footer';
import { typedjson, useTypedLoaderData } from 'remix-typedjson';

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const limit = Number(url.searchParams.get('limit')) || 12;
  const page = Number(url.searchParams.get('page')) || undefined;
  const selectedCategory = url.searchParams.get('category');

  const { user } = await validateUser(request);
  const categories = await getCategoryList(limit, page);

  return typedjson({
    categories,
    user,
    selectedCategory,
  });
};

const TermsOfUse = () => {
  const data = useTypedLoaderData<typeof loader>();

  return (
    <Layout user={data.user as unknown as NonNullable<Awaited<ReturnType<typeof validateUser>>['user']>}>
      <div className="container mx-auto mb-20 gap-4 px-4 sm:px-6 xl:px-56">
        <div className="flex flex-col items-center justify-center py-4">
          <h1 className="text-4xl">Terms of Use</h1>
        </div>
        <div className="flex flex-col gap-4 pt-2">
          <p className="text-2xl">Website Terms of Use ("Website Terms")</p>
          <p>
            The Website Terms for Best2bid.com are below. Also below is a short summary of the key provisions of our
            terms and your responsibilities as a user of our website, www.best2bid.com (the “Website”).
          </p>
          <p>
            Please read these Website Terms carefully before you start to use the website in order to receive the online
            auction content. By:
          </p>
          {/* Ordered list somehow not working */}
          <ol className="list-decimal pl-6">
            <li>1. accessing the Website and/or any content made available through it; and/or </li>
            <li>2. receiving the online auction content; and/or</li>
            <li>3. registering with Best2bid.com,</li>
          </ol>
          <p>
            You indicate that you accept these Website Terms and that you agree to abide by them, whether or not you
            have sign up an account. If you do not agree, please refrain from using the website. Please note that
            auctioneers, merchants, dealers and other third-parties that sell through the Website may have their own
            website terms with which you may also be obliged to comply depending on your use of the Website. We are not
            a party to their terms and have no obligations or liability under them.
          </p>
        </div>
        <div className="border-grey-100 mb-2 border-b py-4" />
        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">Summary of Website Terms</p>
          <p>
            These Website Terms (together with the Privacy Policy) (“the Terms”) tell you the terms of use under which
            you may use our Website. Please read these carefully. We highlight some key terms below:
          </p>
        </div>
        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">What We Do for You</p>
          <p>
            We provide online auction content on our Website. The Website is only a means of communication to view
            online auction content and/or participate in auction sales. We are not an online auctioneer. By submitting a
            winning bid or otherwise agreeing to purchase an item accessed through the Website, you are entering into a
            valid, legal and binding contract with the Seller (as defined below) to consummate the purchase. Although we
            are not a party to any contract for sale and purchase entered into between you as a bidder and any Seller,
            you agree to permit Best2bid or the Seller to charge your credit card on file in the event you fail to pay
            your invoice, as explained in Section 4, below.
          </p>
          <p>
            We are not in control of the information made available by third-parties, such as auctioneers, merchant and
            dealers, which you may access via the Website. We cannot therefore make any representations, warranties or
            guarantees in connection with what any third-parties may offer for sale through the Website.
          </p>
          <p>
            We do not charge you for your general use of the Website. We are paid by third-parties that sell through our
            Website to provide the Website services to potential bidders.
          </p>
        </div>
        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">What You Agree To Do</p>
          <p>Use the Website in accordance with these Website Terms and our Privacy Policy posted on the Website.</p>
          <p>
            Fulfill your contractual obligations entered into with the Seller (as defined below) upon your agreement to
            purchase any item (whether by placing a winning bid or otherwise) by consummating the purchase upon receipt
            of your invoice from the Seller.
          </p>
          <p>Parties</p>
          <p>
            Best2bid Technology Sdn. Bhd. (the “Website Operator”) You, the user of the Service ("the User"), agree to:
          </p>
          <p>Use the Website in accordance with these Website Terms and our Privacy Policy posted on the Website.</p>
        </div>
        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">1. Definitions</p>
          <p>
            "Content" means all data, information advice and downloaded materials contained or referred to in or
            accessed from the Website.
            <br />
            "Intellectual Property Rights" means patents, trademarks, design rights, database rights, copyright, and all
            other intellectual property rights anywhere in the world, whether registered or not. "Licensor" means any
            licensor of the Website Operator including without limitation any third-parties placing Content on the
            Website.
          </p>
          <p>
            "Seller" means any third-parties that sell through the Website, including without limitation auctioneers,
            merchant and dealers.
            <br />
            "Service" means the provision of Content (which includes without limitation, news data, catalogues, and
            other services) provided on the Website to the User by the Website Operator. Auction services are subject to
            additional terms and conditions which the User is required to register for and accept before being able to
            access and use the auction services (whether for live auctions or otherwise).
          </p>
        </div>
        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">2. Our Agreement</p>
          <p>
            These Website Terms constitute a legally binding agreement between you as the User and the Website Operator
            establishing the terms and conditions under which you may have access to, and use of, the Website and the
            Content.
          </p>
          <p>
            The Content of the Website may be restricted by law in certain countries. It is the responsibility of the
            User to determine what those restrictions are and to observe them.
          </p>
          <p>
            These Website Terms are subject to change. The Website Operator reserves the right to amend them at any
            time. The consent of no other person is required. Notification of any amendment shall be affected by posting
            the amended Website Terms on the Website. The amended Website Terms shall become effective immediately. The
            Terms are also for the benefit of any Sellers who the Website Operator, from time to time, in its sole
            discretion, permits to have the benefit (but not the burden) of these Website Terms.
          </p>
          <p>These Website Terms replace all previous terms and conditions.</p>
        </div>
        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">3. Basis of Use</p>
          <p>
            Upon acceptance of these Website Terms, the User has a non-exclusive, non-transferable, non-sub- licencable,
            non-assignable license to use the Service and download Content for its own personal, non- commercial use in
            order to view the Website only. This license shall include any other material downloaded from the Website
            unless other conditions apply to the use of that material (the "License").
          </p>
          <p>
            The Website Operator, in its sole discretion, has the right to revoke this License at any time and to block
            or prevent a User from accessing or using the Website for any reason. In addition, the Website Operator may
            remove Content from the scope of the License without notice.
          </p>
          <p>The User agrees not to:</p>
          <ol className="list-decimal pl-6">
            <li>
              1. use the Service in any way to either directly or indirectly prejudice the Website Operator's interests
              or those of its Licensors or affiliates and/or the provision and exploitation of the Service in any way;
            </li>
            <li>
              2. copy, reproduce, modify, communicate to the public, or make derivative product from or publicly display
              any material available on or through the Website or the Service (other than to the extent required to view
              the Website);
            </li>
            <li>
              3. create a database in electronic or structured manual form by systematically and/or regularly
              downloading, caching, printing and/or storing the material available on or through the Website or the
              Service (by spidering or otherwise) or enable or permit others to do so.
            </li>
          </ol>
          <p>
            The Website Operator shall make the Service available to Users in such forms as it reasonably deems
            appropriate from time to time.
          </p>
          <p>
            The User shall provide at its own cost suitable access to the internet or equivalent through which the
            Service is intended to be delivered and the User shall assume sole responsibility for the configuration and
            maintenance of that access and the licensing and/or provision of any software required.
          </p>
          <p>
            All products and services contained on or referred to on the Website (including Content that can be
            downloaded) are subject to availability and the Website Operator has the right to withdraw any product or
            service at any time without notice.
          </p>
          <p>
            The Website Operator may update the Website regularly and may change the Content at any time without notice.
            If the need arises, the Website Operator may suspend access to the Website or close it indefinitely. Any
            Content on the Website may be out of date at any given time and the Website Operator is under no obligation
            to update the Content. In addition, the Website Operator, in its sole discretion, may from time to time, and
            for any reason, restrict access to parts of the Website or the entire Website to Users who have registered
            with the Website Operator.
          </p>
          <p>
            The Website Operator reserves the right to make improvements, substitutions, modifications or enhancements
            to any part of the Service at any time.
          </p>
          <p>
            The Content, commentary and other materials displayed on the Website are not intended to amount to advice on
            which reliance should be placed. The Website Operator disclaims all responsibility and liability arising
            from any reliance placed on such materials by any User of the Website, or by anyone who may be informed of
            the Content. Notwithstanding the above, the Content and all other material displayed on the Website is
            subject to the exclusions of liability set out in these Website Terms and the User should not rely on the
            Content or other material displayed on the Website as being accurate or up to date but should independently
            verify all information.
          </p>
          <p>The Website Operator's Privacy Policy from time to time shall form part of these Website Terms.</p>
        </div>
        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">4. Items advertised for sale on the Website</p>
          <p>
            Website Operator may, from to time, provide a resource where catalogues and other sale details from auction
            houses can conveniently be viewed. The Website Operator's role is limited to the provision of
          </p>
          <p>
            the Service for the publication of information on items offered for sale provided by the Sellers ("Items").
          </p>
          <p>
            Items are sold subject to the terms and conditions of the Seller. It is the User's responsibility to obtain
            and to read the Seller's terms and conditions carefully before making a bid or offer to purchase any Item.
          </p>
          <p>
            All items listed are to be sold and purchased in the currency listed by the auction house or dealer that
            listed the items. However, Website Operator may provide a currency conversion tool on its Website to assist
            the User in approximating the sale price of particular items in other currencies. User shall not rely for
            any reason or purpose on calculations made through a currency conversion tool offered by Website Operator.
          </p>
          <p>
            The Website Operator is not a party to any transaction between the User and the Seller for the sale of
            Items; nor is the Website Operator acting as agent for or otherwise on behalf of any Seller. Accordingly,
            the User accepts that it will have no claim or cause of action against the Website Operator with respect of
            the sale (or non-sale) of any Item.
          </p>
          <p>
            For the avoidance of doubt, the Website Operator is not responsible for any errors in information about
            Items on the Website. The Website Operator does not evaluate Items and does not act as a specialist or
            expert on any related subject matter. The Website Operator makes no warranties or representation of any kind
            or nature with respect to Items (including but not limited to representations or warranties as to the
            accuracy of description, genuineness, quality, authorship, attribution, provenance, period, culture, source,
            origin, safety, fitness for purpose or availability for sale). If the User has any questions, then they
            should contact the Seller directly. It is the User's responsibility to understand the Seller's payment and
            shipping terms.
          </p>
          <p>
            Although the Website Operator may endeavor to facilitate electronic or other communications between Users
            and Sellers, it accepts no responsibility and disclaims all liability with respect to the availability or
            performance of any such services.
          </p>
          <p>
            Users must register and agree to the Bidder Terms before they can participate in any auctions (whether live
            or otherwise) or other sales through the Website.
          </p>
          <p>
            To the extent that a User agrees to purchase any item from a Seller (through a winning bid or otherwise) but
            fails to consummate the sale by paying the Seller upon receipt of an invoice from the Seller, the User
            expressly agrees and consents to permit Best2bid or the Seller, in their sole discretion, to charge the
            User’s credit card provided to Best2bid for the full outstanding amount due to the Seller pursuant to such
            invoice. (In the event that Best2bid processes any such payment, Best2bid will forward such funds to the
            Seller.)
          </p>
        </div>
        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">5. Title and intellectual property rights</p>
          <p>
            The Website Operator, or its Licensors are, the owners of all Intellectual Property Rights in relation to
            the Website and its Content and, except to the extent expressly permitted by the License granted under these
            Website Terms, the User may not copy, extract, utilize, download, distribute, re-sell, use or publish
            (including by any means of electronic dissemination or extraction) the Content in any way without the prior
            written consent of the Website Operator.
          </p>
        </div>
        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">6. Warranty</p>
          <p>
            The Website Operator does not provide any warranties with respect to the accuracy, operation or usefulness
            of the Service; nor does it warrant that its operation will be uninterrupted or error free. The Website
            Operator warrants that it shall endeavor to perform its obligations under these Website Terms with
            reasonable care and skill.
          </p>
        </div>
        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">7. Refund Policy</p>
          <p>
            Best2bid is an online auction and marketplace platform that connects buyers with independent merchants,
            auction houses, and sellers. Best2bid does not act as the seller or owner of any listed item.
          </p>
          <p>
            Accordingly, Best2bid does not offer or process refunds on behalf of sellers. All sales are deemed final
            unless otherwise stated by the individual merchant or auction house.
          </p>
          <p>
            Buyers are advised to carefully review the terms and conditions provided by the respective seller or auction
            organizer, including any applicable refund, return, or cancellation policies.
          </p>
          <p>
            In the event of a dispute or issue with a transaction, Best2bid may—at its sole discretion—assist in
            facilitating communication between the buyer and the seller but does not guarantee or enforce any refund,
            return, or compensation.
          </p>
        </div>
        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">8. Exclusions and Limits of Liability</p>
          <p>
            Except to the extent expressly provided in these Website Terms, the Website Operator makes no representation
            or warranty of any kind (express, implied or statutory); nor are any conditions included in relation to this
            Website or the Content. Website Operator excludes and disclaims, to the fullest extent permitted by law, all
            such warranties, representations and conditions.
          </p>
          <p>
            The Content is provided for general information only. Any advice or information received through this
            Website or its Content should not be relied upon as being correct or accurate as it the User's obligation to
            verify independently such matters from primary sources of information and/or by taking professional advice.
          </p>
          <p>
            The User is not entitled to rely on the Content, the Service or any associated service of the Website.
            Except in the case of death or personal injury arising from the Website Operator's own negligence or for the
            Website Operator's fraud, the Website Operator excludes and disclaims all liability in contract, tort
            (including negligence), breach of statutory duty or otherwise for any costs, losses, claims, damages,
            expenses or proceedings incurred or suffered by the User arising directly or indirectly in connection with
            the Website and its Content including without limitation any loss, damage or expense arising from, but not
            limited to, any defect, error, imperfection, fault, mistake or inaccuracy with the Website, its Content, the
            Service or associated services (including but not limited to information, advice, and all content and
            software downloaded from this Website), or due to any unavailability of part or all of the Website or any
            Content, the Service or associated services.
          </p>
          <p>
            For the avoidance of doubt, ANY AND ALL LIABILITY OF THE WEBSITE OPERATOR FOR LOSS OF PROFITS, ECONOMIC
            LOSS, WASTED MANAGEMENT TIME, SPECIAL, INCIDENTAL, INDIRECT, AND/OR CONSEQUENTIAL LOSS OR DAMAGE, IS ALSO
            EXPRESSLY EXCLUDED AND DISCLAIMED TO THE FULLEST EXTENT PERMITTED BY LAW.
          </p>
          <p>
            The Website Operator accepts no liability or responsibility for the use made of the Services by the User or
            those under its control and, in particular but without limitation, the content of any information.
          </p>
          <p>
            The exclusions and limitations of liability in these Website Terms are also for the benefit of the Website
            Operator's affiliates (if any).
          </p>
        </div>
        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">9. Links to Other Internet Websites</p>
          <p>
            The Website Operator aims to provide a high quality of service to its Users through the Website, and for
            information and convenience, the Website Operator may from time to time provide links to other internet
            websites. The inclusion of such hyperlinks, however, does not imply any endorsement, representation or
            warranty regarding those websites or the contents of those websites.
          </p>
          <p>
            The User may not link directly or indirectly, or facilitate the linking of, the Website to any other website
            without the Website Operator's prior written consent provided that no consent is required for links made
            solely for the purposes of operating and providing a bona fide search engine, although the Website Operator
            reserves the right at any time to require any such linking to cease.
          </p>
        </div>
        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">10. Indemnity</p>
          <p>
            The User shall, to the fullest extent permitted by law, fully indemnify, save, defend and hold harmless the
            Website Operator and each of its affiliates (if any) from and against any and all liabilities, demands,
            losses, costs, damages and actual expenses (including reasonable attorney’s fees) suffered or incurred by or
            awarded against the Website Operator or any of its affiliates in consequence of or arising out of any
            actions of the User that are inconsistent with, or are in breach of, these Website Terms (including the
            privacy policy), any relevant national or international laws or regulations or arising out of the User's
            negligent, fraudulent or illegal use of the Service or which infringe upon the rights of others.
          </p>
        </div>
        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">11. Termination</p>
          <p>
            The agreement constituted by these Website Terms, including without limitation the License to use the
            Website and any or all the Content, may be terminated by the Website Operator at any time without cause or
            notice.
          </p>
          <p>
            Any termination of this Agreement will be without prejudice to any other rights or remedies of the Website
            Operator or any of its affiliates under these Website Terms or at law and will not affect its accrued rights
            prior to the date of termination. Clauses 4 through 11 shall survive the termination for any reason of these
            Website Terms.
          </p>
        </div>
        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">12. Ancillary Provisions</p>
          <p>
            <span className="font-bold">Severability</span> If any of the provisions or part of a provision of these
            Website Terms is judged to be illegal or unenforceable, the continuation in full force and effect of the
            remainder of the provisions or provision will not be prejudiced unless the substantive purpose of these
            Website Terms is then frustrated, in which case either party may terminate these Website Terms forthwith on
            written notice.
          </p>
          <p>
            <span className="font-bold">Waiver</span> No forbearance or delay by either party in enforcing its
            respective rights will prejudice or restrict the rights of that party, and no waiver of any such rights or
            of any breach of any contractual terms will be deemed to be a waiver of any other right or of any later
            breach.
          </p>
          <p>
            <span className="font-bold">Assignment/sublicensing</span> The User may not assign or sublicense any of its
            rights, obligations, benefits or interests arising under these Website Terms without the prior written
            consent of the Website Operator.
          </p>
          <p>
            <span className="font-bold">Governing law and jurisdiction</span> These Website Terms (and accordingly all
            visits to the Website) shall be governed by the laws of the State of Malaysia. This is without prejudice to
            the Website Operator's right and that of its affiliates to commence proceedings anywhere in the world
            relating to the protection of its Intellectual Property Rights, those of its Licensors, or its rights under
            these Website Terms.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default TermsOfUse;
